import type { FirebaseOptions } from 'firebase/app';

/**
 * Non-production Firebase web config for unit tests and CI.
 * Values are fake placeholders — not secrets and not tied to a real project.
 * Used only when Vitest runs without VITE_FIREBASE_* env vars.
 */
export const FIREBASE_TEST_CLIENT_CONFIG: FirebaseOptions = {
  apiKey: 'AIzaSy000000000000000000000000000000',
  authDomain: 'donegrading-unit-test.firebaseapp.com',
  projectId: 'donegrading-unit-test',
  storageBucket: 'donegrading-unit-test.appspot.com',
  messagingSenderId: '123456789012',
  appId: '1:123456789012:web:000000000000000000',
};

/** Env slice used by {@link resolveFirebaseClientConfig}; tests pass explicit objects. */
export type FirebaseClientConfigEnv = Partial<
  Pick<
    ImportMetaEnv,
    | 'VITE_FIREBASE_API_KEY'
    | 'VITE_FIREBASE_AUTH_DOMAIN'
    | 'VITE_FIREBASE_PROJECT_ID'
    | 'VITE_FIREBASE_STORAGE_BUCKET'
    | 'VITE_FIREBASE_MESSAGING_SENDER_ID'
    | 'VITE_FIREBASE_APP_ID'
  >
> & {
  readonly MODE?: string;
  readonly VITEST?: boolean;
};

type FirebaseEnvKey = keyof Pick<
  ImportMetaEnv,
  | 'VITE_FIREBASE_API_KEY'
  | 'VITE_FIREBASE_AUTH_DOMAIN'
  | 'VITE_FIREBASE_PROJECT_ID'
  | 'VITE_FIREBASE_STORAGE_BUCKET'
  | 'VITE_FIREBASE_MESSAGING_SENDER_ID'
  | 'VITE_FIREBASE_APP_ID'
>;

function readFirebaseEnv(key: FirebaseEnvKey, env: FirebaseClientConfigEnv): string | undefined {
  const value = env[key];
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function isFirebaseTestRuntime(env: FirebaseClientConfigEnv = import.meta.env): boolean {
  return env.MODE === 'test' || env.VITEST === true;
}

/**
 * Resolves Firebase client config from Vite env vars.
 * In Vitest/CI, falls back to {@link FIREBASE_TEST_CLIENT_CONFIG} when vars are
 * missing so Auth can initialize without real credentials.
 */
export function resolveFirebaseClientConfig(
  env: FirebaseClientConfigEnv = import.meta.env
): FirebaseOptions {
  const fromEnv: FirebaseOptions = {
    apiKey: readFirebaseEnv('VITE_FIREBASE_API_KEY', env),
    authDomain: readFirebaseEnv('VITE_FIREBASE_AUTH_DOMAIN', env),
    projectId: readFirebaseEnv('VITE_FIREBASE_PROJECT_ID', env),
    storageBucket: readFirebaseEnv('VITE_FIREBASE_STORAGE_BUCKET', env),
    messagingSenderId: readFirebaseEnv('VITE_FIREBASE_MESSAGING_SENDER_ID', env),
    appId: readFirebaseEnv('VITE_FIREBASE_APP_ID', env),
  };

  if (!isFirebaseTestRuntime(env)) {
    return fromEnv;
  }

  return {
    apiKey: fromEnv.apiKey ?? FIREBASE_TEST_CLIENT_CONFIG.apiKey,
    authDomain: fromEnv.authDomain ?? FIREBASE_TEST_CLIENT_CONFIG.authDomain,
    projectId: fromEnv.projectId ?? FIREBASE_TEST_CLIENT_CONFIG.projectId,
    storageBucket: fromEnv.storageBucket ?? FIREBASE_TEST_CLIENT_CONFIG.storageBucket,
    messagingSenderId: fromEnv.messagingSenderId ?? FIREBASE_TEST_CLIENT_CONFIG.messagingSenderId,
    appId: fromEnv.appId ?? FIREBASE_TEST_CLIENT_CONFIG.appId,
  };
}
